<script setup lang="ts">
import { computed, onMounted, onUnmounted, shallowRef } from 'vue'
import SiteEditableText from '../../SiteEditableText.vue'
import type { BusinessHoursBlock } from '../../types'

const props = defineProps<{ block: BusinessHoursBlock }>()
const currentTime = shallowRef<Date | null>(null)
let clockInterval: ReturnType<typeof setInterval> | undefined

function formatTime(value: string): string {
  const [hourValue = '0', minute = '00'] = value.split(':')
  const hour = Number(hourValue)
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12

  return `${displayHour}:${minute} ${period}`
}

function minutesFromTime(value: string): number {
  const [hours = '0', minutes = '0'] = value.split(':')
  return (Number(hours) * 60) + Number(minutes)
}

const openStatus = computed(() => {
  if (!props.block.settings.showOpenStatus || !currentTime.value) return null

  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: props.block.settings.timezone || 'America/Toronto',
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(currentTime.value)
    const partValue = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? ''
    const day = props.block.content.days.find((candidate) => candidate.day === partValue('weekday').toLowerCase())

    if (!day || day.closed) return { open: false, label: 'Closed now' }

    const currentMinutes = (Number(partValue('hour')) * 60) + Number(partValue('minute'))
    const opensAt = minutesFromTime(day.opensAt)
    const closesAt = minutesFromTime(day.closesAt)
    const open = closesAt > opensAt
      ? currentMinutes >= opensAt && currentMinutes < closesAt
      : currentMinutes >= opensAt || currentMinutes < closesAt

    return { open, label: open ? `Open until ${formatTime(day.closesAt)}` : 'Closed now' }
  } catch {
    return null
  }
})

onMounted(() => {
  currentTime.value = new Date()
  clockInterval = setInterval(() => { currentTime.value = new Date() }, 60_000)
})

onUnmounted(() => {
  if (clockInterval) clearInterval(clockInterval)
})
</script>

<template>
  <section v-if="block.variant === 'schedule'" :id="block.settings.anchorId" class="site:bg-(--site-color-background) site:py-16 site:@min-[40rem]:py-24">
    <div class="site-site-container site:grid site:items-start site:gap-10 site:@min-[64rem]:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] site:@min-[64rem]:gap-20">
      <div class="site:pt-3">
        <SiteEditableText v-if="block.content.eyebrow" v-model="block.content.eyebrow" as="p" class="site:text-sm site:font-semibold site:text-(--site-color-primary)" />
        <SiteEditableText v-model="block.content.heading" as="h2" class="site-heading site:mt-3 site:text-3xl site:font-bold site:tracking-tight site:@min-[40rem]:text-5xl" />
        <SiteEditableText v-model="block.content.description" as="p" class="site:mt-5 site:text-lg site:leading-8 site:text-(--site-color-muted-text)" />
        <span v-if="openStatus" class="site:mt-7 site:inline-flex site:items-center site:gap-2 site:text-sm site:font-semibold" :class="openStatus.open ? 'site:text-(--site-color-primary)' : 'site:text-(--site-color-muted-text)'">
          <span class="site:size-2 site:rounded-full" :class="openStatus.open ? 'site:bg-(--site-color-primary)' : 'site:bg-(--site-color-muted-text)'" />
          {{ openStatus.label }}
        </span>
        <SiteEditableText v-if="block.content.note && block.settings.showNote !== false" v-model="block.content.note" as="p" class="site:mt-4 site:text-sm site:leading-6 site:text-(--site-color-muted-text)" />
      </div>

      <dl class="site:divide-y site:divide-(--site-color-border) site:border-y site:border-(--site-color-border)">
        <div v-for="day in block.content.days" :key="day.id" class="site:flex site:min-h-14 site:items-center site:justify-between site:gap-5 site:py-3">
          <dt class="site-heading site:font-bold">{{ day.label }}</dt>
          <dd class="site:text-right site:text-sm site:font-medium" :class="day.closed ? 'site:text-(--site-color-muted-text)' : 'site:text-(--site-color-text)'">
            {{ day.closed ? 'Closed' : `${formatTime(day.opensAt)} - ${formatTime(day.closesAt)}` }}
          </dd>
        </div>
      </dl>
    </div>
  </section>

  <section v-else :id="block.settings.anchorId" class="site:bg-(--site-color-background) site:py-16 site:@min-[40rem]:py-24">
    <div class="site-site-container">
      <div class="site:mx-auto site:max-w-4xl site:bg-(--site-color-surface) site:p-7 site:shadow-(--site-shadow-md) site:@min-[40rem]:p-10" :style="{ borderRadius: 'var(--site-radius-lg)' }">
        <div class="site:text-center">
          <SiteEditableText v-if="block.content.eyebrow" v-model="block.content.eyebrow" as="p" class="site:text-sm site:font-semibold site:text-(--site-color-primary)" />
          <SiteEditableText v-model="block.content.heading" as="h2" class="site-heading site:mt-3 site:text-3xl site:font-bold site:tracking-tight site:@min-[40rem]:text-5xl" />
          <SiteEditableText v-model="block.content.description" as="p" class="site:mx-auto site:mt-5 site:max-w-2xl site:text-lg site:leading-8 site:text-(--site-color-muted-text)" />
          <span v-if="openStatus" class="site:mt-6 site:inline-flex site:items-center site:gap-2 site:text-sm site:font-semibold" :class="openStatus.open ? 'site:text-(--site-color-primary)' : 'site:text-(--site-color-muted-text)'">
            <span class="site:size-2 site:rounded-full" :class="openStatus.open ? 'site:bg-(--site-color-primary)' : 'site:bg-(--site-color-muted-text)'" />
            {{ openStatus.label }}
          </span>
        </div>

        <dl class="site:mt-9 site:grid site:gap-x-8 site:@min-[40rem]:grid-cols-2">
          <div v-for="day in block.content.days" :key="day.id" class="site:flex site:min-h-13 site:items-center site:justify-between site:gap-4 site:border-t site:border-(--site-color-border) site:py-3">
            <dt class="site-heading site:text-sm site:font-bold">{{ day.label }}</dt>
            <dd class="site:text-right site:text-sm" :class="day.closed ? 'site:text-(--site-color-muted-text)' : 'site:text-(--site-color-text)'">{{ day.closed ? 'Closed' : `${formatTime(day.opensAt)} - ${formatTime(day.closesAt)}` }}</dd>
          </div>
        </dl>
        <SiteEditableText v-if="block.content.note && block.settings.showNote !== false" v-model="block.content.note" as="p" class="site:mt-6 site:text-center site:text-sm site:text-(--site-color-muted-text)" />
      </div>
    </div>
  </section>
</template>
